import { useEffect, useState } from "react";
import "@/App.css";
import { supabase, BYPASS_AUTH, ensureBypassSession } from "@/services/supabase";
import { getProfile } from "@/services/profile";
import { applyTheme, initLocale } from "@/services/settings";
import { useT } from "@/services/i18n";
import LoginScreen from "@/screens/LoginScreen";
import PrivacyPolicyScreen from "@/screens/PrivacyPolicyScreen";
import OnboardingScreen from "@/screens/OnboardingScreen";
import CaptureScreen from "@/screens/CaptureScreen";
import ExploreScreen from "@/screens/ExploreScreen";
import SettingsScreen from "@/screens/SettingsScreen";
import TabBar from "@/components/TabBar";

// Au tout premier lancement, aligne la langue UI sur celle du navigateur (parmi fr/en/ru).
initLocale();

function App() {
  const { t } = useT();
  const [tab, setTab] = useState("capture");
  const [showSettings, setShowSettings] = useState(false);
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    applyTheme();
    const boot = BYPASS_AUTH
      ? ensureBypassSession().then((s) => s).catch(() => null)
      : supabase.auth.getSession().then(({ data }) => data.session);
    boot.then((s) => {
      setSession(s);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Charge le profil dès qu'une session est active.
  useEffect(() => {
    if (!session) {
      setProfile(null);
      setProfileLoaded(true);
      return;
    }
    setProfileLoaded(false);
    getProfile()
      .then((p) => setProfile(p))
      .catch(() => setProfile(null))
      .finally(() => setProfileLoaded(true));
  }, [session?.user?.id]);

  useEffect(() => {
    const open = () => setShowSettings(true);
    window.addEventListener("open-settings", open);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onTheme = () => applyTheme();
    if (mq.addEventListener) mq.addEventListener("change", onTheme);
    return () => {
      window.removeEventListener("open-settings", open);
      if (mq.removeEventListener) mq.removeEventListener("change", onTheme);
    };
  }, []);

  const needsOnboarding = session && profileLoaded && (!profile || !profile.consent_accepted);
  const booting = !ready || (session && !profileLoaded);

  // Page publique : politique de confidentialité, accessible SANS connexion (hors gate auth).
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") : "";
  if (path === "/confidentialite") {
    return <PrivacyPolicyScreen />;
  }

  return (
    <div className="App mx-auto flex min-h-[100dvh] max-w-md flex-col bg-[var(--rmr-bg)] text-[var(--rmr-text)]" data-testid="app-root">
      {booting && (
        <div className="grid flex-1 place-items-center text-[var(--rmr-text-2)]" data-testid="app-loading">
          {t("app_loading")}
        </div>
      )}

      {!booting && !session && <LoginScreen />}

      {!booting && needsOnboarding && (
        <OnboardingScreen
          onDone={() => {
            getProfile().then((p) => setProfile(p)).catch(() => {});
          }}
        />
      )}

      {!booting && session && !needsOnboarding && (
        <>
          {showSettings ? (
            <SettingsScreen onBack={() => setShowSettings(false)} />
          ) : (
            <>
              {tab === "capture" && <CaptureScreen />}
              {tab === "explore" && <ExploreScreen />}
              <TabBar active={tab} onChange={setTab} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default App;
